import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import { useMsal } from '@azure/msal-react';
import useLlmStream from './useLlmStream';
import {
  getChatHistory,
  deleteChatHistory,
  streamChat,
} from '../../helper/llmApi';

function formatTs(seconds) {
  if (!seconds && seconds !== 0) return '';
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function downloadText(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const element = document.createElement('a');
  element.href = url;
  element.download = filename;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  URL.revokeObjectURL(url);
}

function MessageBubble({ role, content }) {
  const isUser = role === 'user';
  return (
    <Box
      display="flex"
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      mb={1}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: '85%',
          px: 2,
          py: 1,
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: 14,
        }}
      >
        {content}
      </Paper>
    </Box>
  );
}

export default function ChatPanel({ jobId }) {
  const { instance } = useMsal();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const { text, isStreaming, error, sources, run, abort, reset } = useLlmStream();
  const scrollContainerRef = useRef(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingHistory(true);
      setHistoryError(null);
      try {
        const data = await getChatHistory(instance, jobId);
        if (!active) return;
        setMessages(data?.messages || []);
      } catch (e) {
        if (!active) return;
        setHistoryError(e.message || 'Konnte Chat-Verlauf nicht laden');
      } finally {
        if (active) setLoadingHistory(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [instance, jobId]);

  // Nur INNERHALB des Chat-Containers scrollen – niemals das Fenster.
  // Beim ersten Render (History-Load) gar nicht scrollen, damit die Seite
  // nicht automatisch zum Chat nach unten springt.
  useEffect(() => {
    if (loadingHistory) return;
    const c = scrollContainerRef.current;
    if (!c) return;
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      c.scrollTop = c.scrollHeight;
      return;
    }
    c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
  }, [messages, text, loadingHistory]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const question = input.trim();
    if (!question || isStreaming) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: question }]);

    await run(async ({ signal, onEvent }) => {
      await streamChat({ instance, jobId, question, onEvent, signal });
    });
  };

  useEffect(() => {
    if (!isStreaming && text) {
      setMessages((prev) => [...prev, { role: 'assistant', content: text }]);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  const handleReset = async () => {
    abort();
    try {
      await deleteChatHistory(instance, jobId);
    } catch (e) {
      console.error('Reset fehlgeschlagen:', e);
    }
    setMessages([]);
    reset();
  };

  const downloadChat = () => {
    const allMessages = text
      ? [...messages, { role: 'assistant', content: text }]
      : messages;
    const content = allMessages
      .map((m) => `${m.role === 'user' ? 'Ich' : 'KI-Chat'}:\n${m.content}`)
      .join('\n\n---\n\n');
    downloadText(`ki-chat-${jobId}.txt`, content);
  };

  const hasDownloadableChat = messages.length > 0 || Boolean(text);

  return (
    <Box display="flex" flexDirection="column" height={{ xs: 520, md: 600 }}>
      <Box
        ref={scrollContainerRef}
        flex={1}
        overflow="auto"
        px={1}
        py={1}
        sx={{ bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
      >
        {loadingHistory ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : historyError ? (
          <Typography color="error" variant="body2">{historyError}</Typography>
        ) : messages.length === 0 && !isStreaming && !text ? (
          <Box textAlign="center" color="text.secondary" py={6}>
            <Typography variant="body2">
              Stelle eine Frage zu diesem Transkript.
            </Typography>
            <Typography variant="caption">
              Die Antwort basiert ausschließlich auf dem Transkript-Inhalt.
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} />
            ))}
            {(isStreaming || text) && (
              <MessageBubble role="assistant" content={text || '…'} />
            )}
          </>
        )}
      </Box>

      {sources && sources.length > 0 && (
        <Box mt={1} mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            Relevante Ausschnitte:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5, mt: 0.5 }}>
            {sources.slice(0, 6).map((s, idx) => (
              <Tooltip key={idx} title={s.text?.slice(0, 260) || ''}>
                <Chip
                  size="small"
                  label={`${formatTs(s.start)}–${formatTs(s.end)}`}
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" mt={1}>
          {error}
        </Typography>
      )}

      <Box component="form" onSubmit={submit} display="flex" gap={1} mt={1}>
        <TextField
          fullWidth
          size="small"
          placeholder="Frage zum Transkript stellen…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={isStreaming}
          multiline
          maxRows={4}
        />
        {isStreaming ? (
          <IconButton color="warning" onClick={abort} aria-label="Stoppen">
            <StopIcon />
          </IconButton>
        ) : (
          <IconButton color="primary" type="submit" disabled={!input.trim()} aria-label="Senden">
            <SendIcon />
          </IconButton>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={downloadChat}
          disabled={!hasDownloadableChat}
        >
          Chat herunterladen
        </Button>
        <Button
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
          disabled={isStreaming || messages.length === 0}
        >
          Verlauf zurücksetzen
        </Button>
      </Box>
    </Box>
  );
}
